<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">ALL SHIPMENTS</h1>
      </v-row>

      <!-- Add Shipment Button -->
      <v-row justify="center" class="mb-4">
        <v-btn
          large
          @click="navigateToAddShipment"
          class="nexus-btn-white"
        >
          <v-icon left>mdi-truck-plus</v-icon>
          Add Shipment
        </v-btn>
      </v-row>

      <!-- Content Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="11" lg="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
              </v-overlay>

              <!-- Search & Filter Section -->
              <div class="section-header mb-6">
                <h2 class="section-title">FILTER & SEARCH</h2>
                <div class="section-divider"></div>
              </div>

              <v-row class="mb-6">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="search"
                    label="Search Shipments"
                    dense outlined
                    prepend-inner-icon="mdi-magnify"
                    class="nexus-input"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="selectedStatus"
                    :items="statusOptions"
                    label="Filter by Status"
                    dense outlined
                    prepend-inner-icon="mdi-filter"
                    class="nexus-input"
                    hide-details
                  />
                </v-col>
              </v-row>

              <!-- Shipments Grid Section -->
              <div class="section-header mb-4">
                <h2 class="section-title">SHIPMENTS</h2>
                <div class="section-divider"></div>
              </div>

              <!-- Shipments Grid -->
              <v-row v-if="!loading">
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  v-for="shipment in filteredShipments"
                  :key="shipment.id"
                >
                  <v-card 
                    :class="['shipment-card', { 'open-order-card': shipment.shipmentNumber === 'OPEN_ORDER' }]" 
                    elevation="4" 
                    hover
                    @click="openShipment(shipment)"
                  >
                    <!-- Header Section -->
                    <div class="card-header">
                      <v-chip
                        :color="shipment.shipmentNumber === 'OPEN_ORDER' ? 'indigo' : 'primary'"
                        dark
                        label
                        small
                        class="shipment-chip"
                      >
                        {{ shipment.shipmentNumber === 'OPEN_ORDER' ? 'OPEN ORDER' : `Shipment #${shipment.shipmentNumber}` }}
                      </v-chip>
                      <v-chip
                        :color="getStatusColor(shipment.status)"
                        dark
                        label
                        small
                        class="status-chip"
                      >
                        {{ shipment.status || "Unknown" }}
                      </v-chip>
                    </div>

                    <!-- Content Section -->
                    <v-card-text class="card-content">
                      <div class="info-row">
                        <span class="info-label">Supplier:</span>
                        <span class="info-value">{{ shipment.supplierName || "N/A" }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Method:</span>
                        <span class="info-value">{{ shipment.method || "N/A" }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Expected Arrival:</span>
                        <span class="info-value">{{ formatDate(shipment.expectedArrivalDate) || "N/A" }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">
                          {{ shipment.shipmentNumber === 'OPEN_ORDER' ? 'Available SOs:' : 'Linked SOs:' }}
                        </span>
                        <span class="info-value">
                          {{ shipment.linkedSOCount || '0' }} SOs
                        </span>
                      </div>
                    </v-card-text>

                    <!-- Card Action -->
                    <v-card-actions class="card-actions">
                      <v-btn
                        color="primary"
                        block
                        class="nexus-btn action-btn"
                        @click.stop="openShipment(shipment)"
                      >
                        <v-icon left small>{{ getButtonIcon(shipment) }}</v-icon>
                        {{ getButtonText(shipment) }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <!-- No Results -->
              <v-row v-if="!loading && filteredShipments.length === 0" justify="center" class="mt-8">
                <v-col cols="12" class="text-center">
                  <div class="no-results">
                    <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-truck-outline</v-icon>
                    <h3 class="no-results-title">No shipments found</h3>
                    <p class="no-results-text">Try adjusting your search or filter criteria</p>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Confirm Back Dialog -->
    <v-dialog v-model="backDialog" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Any unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="goBack">
            <v-icon left>mdi-check</v-icon>Yes
          </v-btn>
          <v-btn color="grey" text @click="backDialog = false">
            <v-icon left>mdi-close</v-icon>Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const db = getFirestore(firebaseApp);

export default {
  name: "AllShipments",
  data() {
    return {
      shipments: [],
      loading: true,
      search: "",
      selectedStatus: "All",
      statusOptions: [
        "All",
        "New Order",
        "Referenced", 
        "Booked",
        "Manufacturing",
        "In Transit",
        "Docked",
        "At Airport",
        "On Route to JHB",
        "Cancelled",
        "Delivered"
      ],
      backDialog: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      authStateUnsubscribe: null,
    };
  },
  
  async created() {
    // Setup auth state listener
    this.authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
    });

    await this.fetchShipments();
  },

  beforeDestroy() {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }
  },
  
  computed: {
    filteredShipments() {
      let filtered = this.shipments;

      if (this.search) {
        const q = this.search.toLowerCase();
        filtered = filtered.filter((s) =>
          [s.shipmentNumber, s.supplierName, s.method, s.status]
            .some((field) => field?.toString().toLowerCase().includes(q))
        );
      }

      if (this.selectedStatus && this.selectedStatus !== "All") {
        filtered = filtered.filter((s) => s.status === this.selectedStatus);
      }

      return filtered;
    },
  },
  
  methods: {
    handleError(error, operation) {
      console.error(`Error in ${operation}:`, error);
      
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        this.$router.push('/login');
        return;
      }
      
      this.snackbarMessage = `${operation} failed: ${error.message}`;
      this.snackbarColor = "error";
      this.snackbar = true;
    },

    async fetchShipments() {
      try {
        this.loading = true;
        
        // First, fetch all SOs to check their statuses and shipment assignments
        const salesOrdersSnap = await getDocs(collection(db, "SalesTracker"));
        const soData = {};
        salesOrdersSnap.forEach(doc => {
          const data = doc.data();
          soData[data.salesOrderNumber] = {
            status: data.status,
            docId: doc.id
          };
        });
        
        // Get all shipments to see which SOs are on actual shipments
        const allShipmentsSnap = await getDocs(collection(db, "shipments"));
        const sosOnActualShipments = new Set();
        
        allShipmentsSnap.forEach(doc => {
          const data = doc.data();
          // Only count SOs on non-Open Order shipments
          if (data.shipmentNumber !== 'OPEN_ORDER') {
            const linkedSOs = data.linkedSONumbers || [];
            linkedSOs.forEach(so => sosOnActualShipments.add(so));
          }
        });
        
        const list = [];
        allShipmentsSnap.forEach((docSnap) => {
          const d = docSnap.data();
          
          let linkedSONumbers = d.linkedSONumbers || [];
          
          // For Open Order: only show SOs that are NOT on any actual shipments
          if (d.shipmentNumber === 'OPEN_ORDER') {
            linkedSONumbers = linkedSONumbers.filter(so => {
              // Keep SO if it's NOT on any actual shipment
              return !sosOnActualShipments.has(so);
            });
          }
          
          list.push({
            id: docSnap.id,
            shipmentNumber: d.shipmentNumber,
            supplierName: d.supplierName,
            method: d.method,
            expectedArrivalDate: d.expectedArrivalDate,
            status: d.status,
            linkedSOCount: linkedSONumbers.length,
          });
        });

        this.shipments = list.sort((a, b) => {
          if (a.shipmentNumber === 'OPEN_ORDER') return -1;
          if (b.shipmentNumber === 'OPEN_ORDER') return 1;
          
          const aNum = parseInt(a.shipmentNumber) || 0;
          const bNum = parseInt(b.shipmentNumber) || 0;
          return bNum - aNum;
        });
      } catch (error) {
        this.handleError(error, "Fetch Shipments");
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return dateString;
      }
    },

    getStatusColor(status) {
      const statusColors = {
        "New Order": "blue-grey",
        "Referenced": "indigo",
        "Booked": "blue",
        "Manufacturing": "orange",
        "In Transit": "purple",
        "Docked": "teal",
        "At Airport": "cyan",
        "On Route to JHB": "light-blue",
        "Cancelled": "red",
        "Delivered": "green",
      };
      return statusColors[status] || "grey";
    },

    getButtonIcon(shipment) {
      if (shipment.status === "Delivered") return "mdi-eye";
      if (shipment.shipmentNumber === "OPEN_ORDER") return "mdi-cog";
      return "mdi-pencil";
    },

    getButtonText(shipment) {
      if (shipment.status === "Delivered") return "View";
      if (shipment.shipmentNumber === "OPEN_ORDER") return "Manage Orders";
      return "View/Edit";
    },

    openShipment(shipment) {
      localStorage.setItem("selectedShipmentId", shipment.id);
      localStorage.setItem("shipmentViewMode", shipment.status === "Delivered" ? "view-only" : "edit");
      this.$router.push({ name: "EditShipment" });
    },

    navigateToAddShipment() {
      this.$router.push({ name: "AddShipment" });
    },

    confirmBackNavigation() {
      this.backDialog = true;
    },

    goBack() {
      this.backDialog = false;
      this.$router.push({ name: "HomePage" });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  padding-bottom: 60px;
  position: relative;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-btn-white {
  background: white !important;
  color: #334155 !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-white:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(0, 0, 0, 0.3) !important;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 20px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.3) !important;
}

.section-header {
  position: relative;
  margin-bottom: 24px;
}

.section-title {
  color: #1e293b;
  font-weight: 700;
  font-size: 1.3rem;
  letter-spacing: 1.5px;
  margin-bottom: 8px;
}

.section-divider {
  height: 3px;
  width: 60px;
  background: linear-gradient(90deg, #475569 0%, #94a3b8 100%);
  border-radius: 2px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.shipment-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 2px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 10px 30px -5px rgba(71, 85, 105, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 280px;
}

.shipment-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px -8px rgba(71, 85, 105, 0.3);
  border-color: #475569;
}

.open-order-card {
  border: 2px solid #3f51b5 !important;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%) !important;
}

.open-order-card:hover {
  border-color: #303f9f !important;
  box-shadow: 0 20px 40px -8px rgba(63, 81, 181, 0.4) !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 16px 8px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  gap: 8px;
}

.shipment-chip {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  max-width: 60%;
}

.status-chip {
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  border-radius: 8px !important;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
  padding: 16px !important;
  background: rgba(255, 255, 255, 0.8);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: rgba(248, 250, 252, 0.7);
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid rgba(226, 232, 240, 0.5);
}

.info-row:hover {
  background: rgba(241, 245, 249, 0.9);
  border-color: rgba(71, 85, 105, 0.2);
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.info-value {
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-actions {
  padding: 12px 16px 16px 16px !important;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 1px solid rgba(226, 232, 240, 0.6);
}

.action-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  font-size: 0.875rem !important;
  min-height: 40px !important;
}

.no-results {
  padding: 40px 20px;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 16px;
  border: 2px dashed rgba(148, 163, 184, 0.5);
}

.no-results-title {
  color: #64748b;
  font-weight: 600;
  margin-bottom: 8px;
}

.no-results-text {
  color: #94a3b8;
  margin: 0;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.back-button .v-icon {
  color: white !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

/* Form validation styles */
.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

/* Loading overlay */
.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .nexus-title {
    font-size: 2rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
  
  .shipment-card {
    margin-bottom: 16px;
    min-height: 260px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px;
  }
  
  .shipment-chip,
  .status-chip {
    max-width: 100%;
    text-align: center;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 8px 10px;
  }
  
  .info-value {
    max-width: 100%;
    text-align: left;
    font-size: 0.85rem;
  }
  
  .info-label {
    font-size: 0.8rem;
  }
}

/* Grid adjustments for better spacing */
@media (min-width: 1200px) {
  .v-col {
    max-width: 300px;
  }
}

@media (min-width: 1400px) {
  .v-col {
    max-width: 280px;
  }
}

/* Enhanced hover effects */
.shipment-card .v-chip {
  transition: all 0.2s ease;
}

.shipment-card:hover .v-chip {
  transform: scale(1.02);
}

.shipment-card:hover .info-row {
  background: rgba(241, 245, 249, 1);
}

/* Focus states for accessibility */
.shipment-card:focus {
  outline: 3px solid rgba(71, 85, 105, 0.3);
  outline-offset: 2px;
}
</style>